/**
 * @GestionalePD Admin Survey Detail page UI
 */
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import axios from 'axios';
import {
  Container,
  Grid,
  Card,
  Typography,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
//-----------------------------------------------------------------------------------------------
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { CustomDatePicker, CustomTextField } from '../../utils/StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton, CustomIconButton } from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { LoadingUI } from '../../utils/LayoutStyles';
import API from '../../api';

/**
 * @returns Admin Q&A page UI
 */

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function SurveyDetail() {
  // General
  const { state } = useLocation();
  const surveyID = state.surveyID;
  const [apiCalled, setApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Survey Info
  const [surveyInfo, setSurveyInfo] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [entity, setEntity] = useState({});
  const [onlyCardHolder, setOnlyCardHolder] = useState(0);
  const [surveyName, setSurveyName] = useState('');
  const [question, setQuestion] = useState('');
  const [surveyStatus, setSurveyStatus] = useState(0);
  const [surveyAnswer, setSurveyAnswer] = useState([]);
  const [edit, setEdit] = useState(false);

  // Get Initial APIS
  useEffect(() => {
    getInitialData();
  }, []);

  // Get Initial Survey Info and Entities List
  const getInitialData = () => {
    axios
      .all([API.get(`/surveys/${surveyID}?expand`)])
      .then(
        axios.spread((surveyResponse) => {
          setSurveyInfo(surveyResponse.data.survey);
          setStartDate(surveyResponse.data.survey.start_date);
          setEndDate(surveyResponse.data.survey.end_date);
          setOnlyCardHolder(surveyResponse.data.survey.only_card_holder);
          setSurveyName(surveyResponse.data.survey.survey_name);
          setQuestion(surveyResponse.data.survey.survey_question);
          setSurveyStatus(surveyResponse.data.survey.survey_status);
          setSurveyAnswer(surveyResponse.data.survey.survey_answers);
          setApiCalled(true);
        })
      )
      .catch((errors) => {
        setApiCalled(true);
        HttpErrorHandler(errors, setErrorMessage);
      });
  };

  // Get Survey Info
  const getSurvey = () => {
    API.get(`/surveys/${surveyID}?expand`)
      .then((response) => {
        setSurveyInfo(response.data.survey);
        setStartDate(response.data.survey.start_date);
        setEndDate(response.data.survey.end_date);
        setOnlyCardHolder(response.data.survey.only_card_holder);
        setSurveyName(response.data.survey.survey_name);
        setQuestion(response.data.survey.survey_question);
        setSurveyStatus(response.data.survey.survey_status);
        setSurveyAnswer(response.data.survey.survey_answers);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  /* ----------------------------- Entities Dialog ---------------------------- */

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    setOpen(false);
  };

  const [selectedEntity, setSelectedEntity] = useState(null);

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  useEffect(() => {
    if (selectedEntity) {
      setEntity(selectedEntity);
    }
  }, [selectedEntity]);

  /* ------------------------------- Edit survey ------------------------------ */

  // Handle Close Campaign Data Edit Button
  const handleCloseEditButtonClick = () => {
    setEdit(false);
  };

  // Edit Survey Info
  const editSurvey = (e) => {
    e.preventDefault();

    setEdit(true);

    if (edit) {
      const editSurveyData = {
        survey_name: surveyName,
        survey_question: question,
        entity_id: entity.id,
        survey_status: surveyStatus,
        start_date: dayjs(startDate).format('YYYY/MM/DD'),
        end_date: dayjs(endDate).format('YYYY/MM/DD'),
        only_card_holder: onlyCardHolder,
      };

      let itemsLeft = false;

      Object.keys(editSurveyData).forEach((key) => {
        const value = editSurveyData[key];

        if (Object.prototype.hasOwnProperty.call(editSurveyData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            itemsLeft = true;
            handleOpenEditSurveySnack();
          }
        }
      });

      if (!itemsLeft) {
        if (dayjs(editSurveyData.end_date).isBefore(dayjs(editSurveyData.start_date))) {
          showPopup(false, 'La data di fine non può essere antecedente a quella di inizio');
        } else {
          API.put(`/surveys/${surveyID}`, editSurveyData)
            .then((response) => {
              showPopup(true, 'Informazioni salvate');
              setEdit(false);
            })
            .catch((error) => {
              HttpErrorHandler(error, setPopUpText);
              showPopup(false);
            });
        }
      }
    }
  };

  /* ------------------------------- New Answer ------------------------------- */
  const [newAnswer, setNewAnswer] = useState('');
  const [openNewAnswerDialog, setOpenNewAnswerDialog] = useState(false);

  // Open New Answer Dialog
  const handleOpenNewAnswerDialog = () => {
    setIsDialogOpen('newAnswer');
    setOpenNewAnswerDialog(true);
  };

  // Close New Answer Dialog
  const handleCloseNewAnswerDialog = () => {
    setOpenNewAnswerDialog(false);
    setIsDialogOpen('noDialog');
    setOpen(false);
  };

  // Create New Answer
  const createNewAnswer = (e) => {
    e.preventDefault();

    const answerData = {
      survey_id: surveyID,
      answer_name: newAnswer,
    };

    if (answerData.answer_name)
      API.post('/survey_answers', answerData)
        .then((response) => {
          showPopup(true, 'Informazioni salvate');
          getSurvey();

          setTimeout(() => {
            handleCloseNewAnswerDialog();
          }, POPUP_DURATION_DELAY);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(true);
        });
  };

  /* ------------------------------- Edit Answer ------------------------------ */
  const [answerToEdit, setAnswerToEdit] = useState({});
  const [answerToEditName, setAnswerToEditName] = useState('');
  const [answerToEditIndex, setAnswerToEditIndex] = useState(0);
  const [openEditAnswerDialog, setOpenEditAnswerDialog] = useState(false);

  // Open Edit Answer Dialog
  const handleOpenEditAnswerDialog = (answer, index) => {
    setIsDialogOpen('editAnswer');
    setAnswerToEdit(answer);
    setAnswerToEditName(answer.answer_name);
    setAnswerToEditIndex(index);
    setOpenEditAnswerDialog(true);
  };

  // Close Edit Answer Dialog
  const handleCloseEditAnswerDialog = () => {
    setAnswerToEdit({});
    setAnswerToEditName('');
    setAnswerToEditIndex(0);
    setOpenEditAnswerDialog(false);
    setIsDialogOpen('noDialog');
    setOpen(false);
  };

  // Edit Answer Info
  const editAnswer = (e) => {
    e.preventDefault();

    const editAnswerData = {
      survey_id: answerToEdit.survey_id,
      answer_name: answerToEditName,
    };

    if (
      editAnswerData.answer_name !== '' &&
      editAnswerData.answer_name !== null &&
      editAnswerData.answer_name !== undefined &&
      editAnswerData.answer_name.length > 0
    ) {
      API.put(`/survey_answers/${answerToEdit.id}`, editAnswerData)
        .then((response) => {
          showPopup(true, 'Informazioni salvate');
          getSurvey();

          setTimeout(() => {
            handleCloseEditAnswerDialog();
          }, POPUP_DURATION_DELAY);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      handleOpenEditAnswerSnack();
    }
  };

  // Snackbars
  const {
    open: openEditSurveySnack,
    handleOpen: handleOpenEditSurveySnack,
    handleClose: handleCloseEditSurveySnack,
  } = CreateSnackbarFunctions(false);

  const {
    open: openEditAnswerSnack,
    handleOpen: handleOpenEditAnswerSnack,
    handleClose: handleCloseEditAnswerSnack,
  } = CreateSnackbarFunctions(false);

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState('noDialog');

  // Show Popup
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);
    setPopUpText(text);

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}  >
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  return (
    <div>
      <Helmet>
        <title>Questionari</title>
      </Helmet>

      {errorMessage.length === 0 ? (
        <Container maxWidth="xl">
          <Card sx={{ padding: '24px' }}>
            <EntitiesDialog
              openDialog={openEntitiesFilter}
              closeDialog={handleCloseEntitiesFilterDialog}
              outputEntity={getSelectedEntity}
              context={'survey'}
            />

            {apiCalled ? (
              <form onSubmit={editSurvey}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: 'bold' }}>Dati Questionario</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <CustomTextField
                      disabled={!edit}
                      label="Nome Questionario"
                      value={surveyName}
                      onChange={(e) => setSurveyName(e.currentTarget.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomTextField
                      disabled={!edit}
                      required
                      value={question}
                      onChange={(e) => setQuestion(e.currentTarget.value)}
                      rows="3"
                      fullWidth
                      multiline
                      label="Domanda questionario"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <>
                      <CustomTextField
                        required
                        disabled={!edit}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                              <Tooltip title="Modifica" placement="left" arrow>
                                <EditIcon
                                  onClick={() => {
                                    if (edit) {
                                      setOpenEntitiesFilter(!openEntitiesFilter);
                                    }
                                  }}
                                />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        value={entity.name}
                        label="Circolo"
                      />
                    </>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          disabled={!edit}
                          required
                          value={dayjs(startDate)}
                          maxDate={dayjs(endDate)}
                          onChange={(date) => setStartDate(date)}
                          label="Data Inizio"
                          format="DD/MM/YYYY"
                        />
                      </LocalizationProvider>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          disabled={!edit}
                          value={dayjs(endDate)}
                          minDate={dayjs(startDate)}
                          required
                          onChange={(date) => setEndDate(date)}
                          label="Data Fine"
                          format="DD/MM/YYYY"
                        />
                      </LocalizationProvider>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      disabled={!edit}
                      required
                      select
                      label="Autorizzazione Voto"
                      value={onlyCardHolder}
                      onChange={(e) => setOnlyCardHolder(e.target.value)}
                    >
                      <MenuItem value={0}>Chiunque</MenuItem>
                      <MenuItem value={1}>Solo Tesserati</MenuItem>
                    </CustomTextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CustomTextField
                      disabled={!edit}
                      required
                      select
                      label="Stato Questionario"
                      value={surveyStatus}
                      onChange={(e) => setSurveyStatus(e.target.value)}
                    >
                      <MenuItem value={1}>Aperto</MenuItem>
                      <MenuItem value={0}>Chiuso</MenuItem>
                    </CustomTextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="end" gap="12px">
                      {edit && (
                        <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>Annulla</DefaultOutlinedButton>
                      )}
                      <DefaultContainedButton type="submit">{edit ? 'Salva' : 'Modifica'}</DefaultContainedButton>
                      <CustomSnackbar open={openEditSurveySnack} handleClose={handleCloseEditSurveySnack} />
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            ) : (
              LoadingUI()
            )}

            {isDialogOpen === 'noDialog' && displayPopup()}
          </Card>

          <Card sx={{ padding: '24px', marginTop: '24px' }}>
            {apiCalled ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 'bold' }}>Risposte Questionario</Typography>
                    <DefaultContainedButton onClick={handleOpenNewAnswerDialog}>
                      Aggiungi Risposta
                    </DefaultContainedButton>
                    <Dialog open={openNewAnswerDialog} fullWidth onClose={() => {}}>
                      <DialogTitle>Nuova Risposta</DialogTitle>
                      <DialogContent sx={{ paddingTop: '10px !important' }}>
                        <CustomTextField
                          label="Risposta"
                          multiline
                          rows={3}
                          onChange={(e) => setNewAnswer(e.currentTarget.value)}
                        />
                      </DialogContent>
                      <DialogActions>
                        <DefaultContainedButton onClick={createNewAnswer}>Crea</DefaultContainedButton>
                        <DefaultOutlinedButton onClick={handleCloseNewAnswerDialog}>Chiudi</DefaultOutlinedButton>
                        {isDialogOpen === 'newAnswer' && displayPopup()}
                      </DialogActions>
                    </Dialog>
                  </Stack>
                </Grid>

                {surveyAnswer.length > 0 ? (
                  surveyAnswer.map((answer, index) => (
                    <Grid item key={answer.answer_name} xs={12}>
                      <Stack direction="row" justifyContent="center" alignItems="center" gap="24px">
                        <CustomTextField disabled label={`Risposta ${index + 1}`} value={answer.answer_name} />
                        <CustomIconButton
                          sx={{
                            borderRadius: '8px',
                            height: '56px',
                            width: '56px',
                            backgroundColor: '#f6f6f6',
                            '&:hover': {
                              backgroundColor: '#e6e6e6',
                            },
                          }}
                          onClick={() => handleOpenEditAnswerDialog(answer, index)}
                        >
                          <EditIcon />
                        </CustomIconButton>
                      </Stack>

                      <Dialog fullWidth open={openEditAnswerDialog} onClose={() => {}}>
                        <DialogTitle>Modifica Risposta</DialogTitle>
                        <DialogContent sx={{ paddingTop: '10px !important' }}>
                          <CustomTextField
                            required
                            label={`Risposta ${answerToEditIndex + 1}`}
                            multiline
                            rows={3}
                            value={answerToEditName}
                            onChange={(e) => setAnswerToEditName(e.currentTarget.value)}
                          />
                        </DialogContent>
                        <DialogActions>
                          <DefaultContainedButton onClick={editAnswer}>Modifica</DefaultContainedButton>
                          <DefaultOutlinedButton onClick={handleCloseEditAnswerDialog}>Chiudi</DefaultOutlinedButton>
                          {isDialogOpen === 'editAnswer' && displayPopup()}
                        </DialogActions>

                        <CustomSnackbar open={openEditAnswerSnack} handleClose={handleCloseEditAnswerSnack} />
                      </Dialog>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    Nessuna risposta disponibile
                  </Grid>
                )}
              </Grid>
            ) : (
              LoadingUI()
            )}
          </Card>
        </Container>
      ) : (
        <Container maxWidth="xl">{ErrorUI(errorMessage)}</Container>
      )}
    </div>
  );
}
